import { type ReactNode, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import HelpIcon from '@mui/icons-material/Help'
import LaunchIcon from '@mui/icons-material/Launch'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import IconButton from '@mui/material/IconButton'
import Paper from '@mui/material/Paper'
import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableRow from '@mui/material/TableRow'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import makeStyles from '@mui/styles/makeStyles'
import type { ColDef, ColGroupDef, IDatasource, IGetRowsParams } from 'ag-grid-community'
import { AgGridReact, type CustomCellRendererProps } from 'ag-grid-react'

import { type ApiContract, type InferResponseBody, api, getApiErrorMessage, queryKeys } from '~/api'
import FixedBox from '~/components/FixedBox'
import StockItemFilter from '~/stock/components/stock-item-filter'
import { useAppSelector } from '~/store'

import 'ag-grid-community/styles/ag-grid.css'
import 'ag-grid-community/styles/ag-theme-quartz.css'

type SelectedStock = {
    channelStockItemId: number
    isBundle: boolean
}

type Data = InferResponseBody<ApiContract['stock']['stockItemsWithStock']>['results'][0]

export default function StockByShopPage() {
    const [selectedStock, setSelectedStock] = useState<SelectedStock | null>(null)
    const [isOpen, setIsOpen] = useState(false)

    const handleBreakdownClick = useCallback((stock: SelectedStock) => {
        setIsOpen(true)
        setSelectedStock(stock)
    }, [])

    return (
        <FixedBox
            component={Paper}
            sx={{
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                height: '100%',
            }}
        >
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <StockItemFilter debounceTime={500} />
            </Box>
            <TableContainer
                sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    '& > div': { height: '100%' },
                }}
            >
                <ChannelStockDataTable onBreakdownClick={handleBreakdownClick} />
            </TableContainer>
            {selectedStock && (
                <Dialog
                    fullWidth
                    maxWidth="md"
                    open={isOpen}
                    TransitionProps={{ onExited: () => setSelectedStock(null) }}
                    onClose={() => setIsOpen(false)}
                >
                    <DialogTitle>{gettext('Channel stock item breakdown')}</DialogTitle>
                    <DialogContent>
                        <TableContainer
                            sx={{
                                border: '1px solid',
                                borderColor: 'grey.200',
                                borderRadius: 2,
                            }}
                        >
                            <StockBreakdownTable
                                channelStockItemId={selectedStock.channelStockItemId}
                                isBundle={selectedStock.isBundle}
                            />
                        </TableContainer>
                    </DialogContent>
                    <DialogActions>
                        <Button variant="text" onClick={() => setIsOpen(false)}>
                            Close
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
        </FixedBox>
    )
}

const useStyles = makeStyles({
    row: {
        '&:hover svg': {
            transition: 'opacity 100ms ease-in-out',
            opacity: 1,
        },
    },
})

const FETCH_LIMIT = 50

type StockBreakdownTableProps = {
    channelStockItemId: number
    isBundle: boolean
}

function StockBreakdownTable({ channelStockItemId, isBundle }: StockBreakdownTableProps) {
    const { t } = useTranslation('channel_stock_breakdown')

    const { data, error, status } = api.stock.channelStockItemBreakdown.useQuery(
        queryKeys.stock.breakdown(channelStockItemId).queryKey,
        { params: { id: channelStockItemId } }
    )

    function formatCampaignType(type: string) {
        switch (type) {
            case 'flash_sale':
                return 'Flash Sale'
            default:
                return type
        }
    }

    if (status === 'loading') {
        return (
            <Stack spacing={2}>
                {Array.from({ length: 6 }).map((_, index) => (
                    <Stack key={index} gap={2} direction="row">
                        <Skeleton variant="text" width={100} />
                        <Skeleton variant="text" width={200} />
                    </Stack>
                ))}
            </Stack>
        )
    }

    if (status === 'error') {
        return <Typography color="error">{getApiErrorMessage(error.body)}</Typography>
    }

    const {
        active_channel_stock_item_campaign_locks,
        active_channel_stock_adjustment,
        actual_stock,
        bundle_stock_items,
        stock_adjustment,
        buffer_adjustment,
        buffer_threshold,
        calculated_stock,
    } = data.body

    const tooltipTitle = `Display = ${
        bundle_stock_items.length > 0 ? '(Lowest actual stock quantity/units)' : 'Actual stock'
    } ${active_channel_stock_adjustment ? '+ Stock adjustment' : ''} ${
        active_channel_stock_item_campaign_locks.length > 0 ? '- Reserved stocks' : ''
    } - Buffer adjustment, minimum 0`

    return (
        <Table size="small">
            <TableBody>
                {isBundle ? (
                    bundle_stock_items.map(({ stock_item: item, quantity }, index, arr) => (
                        <TableRow
                            key={item.id}
                            sx={{
                                '& > td': {
                                    borderBottomColor:
                                        arr.length - 1 === index ? 'grey.400' : undefined,
                                },
                            }}
                        >
                            <TableCell variant="head" scope="col">
                                {t('actual_stock')}
                                <CaptionText>
                                    {interpolate(gettext('%s - %s units'), [
                                        item.sku,
                                        item.num_of_units,
                                    ])}
                                </CaptionText>
                            </TableCell>
                            <TableCell>{quantity}</TableCell>
                        </TableRow>
                    ))
                ) : (
                    <TableRow>
                        <TableCell variant="head" scope="col">
                            {t('actual_stock')}
                        </TableCell>
                        <TableCell>{actual_stock}</TableCell>
                    </TableRow>
                )}
                {active_channel_stock_item_campaign_locks.map((lock) => {
                    if (lock.units_remaining === 0) {
                        return null
                    }

                    return (
                        <TableRow key={lock.channel_stock_item}>
                            <TableCell variant="head" scope="col">
                                {t('reserved_stock')}
                                <CaptionText>
                                    {lock.channel_name}
                                    {' - '}
                                    {lock.shop_name}
                                    {' - '}
                                    {formatCampaignType(lock.channel_campaign.campaign_type)}
                                </CaptionText>
                            </TableCell>
                            <TableCell>-{Math.abs(lock.units_remaining)}</TableCell>
                        </TableRow>
                    )
                })}
                {active_channel_stock_adjustment && (
                    <TableRow>
                        <TableCell variant="head" scope="col">
                            {t('adjustment')}
                            <CaptionText>
                                {active_channel_stock_adjustment.adjustment_group.name}
                            </CaptionText>
                        </TableCell>
                        <TableCell>
                            {stock_adjustment > 0 ? `+${stock_adjustment}` : stock_adjustment}
                        </TableCell>
                    </TableRow>
                )}
                {(!!buffer_adjustment || !!buffer_threshold) && (
                    <TableRow
                        sx={{
                            '& > td': {
                                borderBottomColor: 'grey.400',
                            },
                        }}
                    >
                        <TableCell variant="head" scope="col">
                            {gettext('Buffer adjustment')}
                        </TableCell>
                        <TableCell>
                            <Stack direction="row" gap={1}>
                                {-buffer_adjustment}
                                <Tooltip
                                    title={interpolate(gettext('Buffer threshold: %s units'), [
                                        buffer_threshold,
                                    ])}
                                >
                                    <HelpIcon fontSize="small" color="action" />
                                </Tooltip>
                            </Stack>
                        </TableCell>
                    </TableRow>
                )}
                <TableRow sx={{ '& > td': { borderBottom: 'none' } }}>
                    <TableCell variant="head" scope="col">
                        {gettext('Display')}
                    </TableCell>
                    <TableCell>
                        <Stack direction="row" gap={1}>
                            {calculated_stock}
                            <Tooltip title={tooltipTitle}>
                                <HelpIcon fontSize="small" color="action" />
                            </Tooltip>
                        </Stack>
                    </TableCell>
                </TableRow>
            </TableBody>
        </Table>
    )
}

type CaptionTextProps = {
    children: ReactNode
}

function CaptionText({ children }: CaptionTextProps) {
    return (
        <Typography variant="caption" sx={{ display: 'block' }}>
            {children}
        </Typography>
    )
}

type ChannelStockDataTableProps = {
    onBreakdownClick: (stock: SelectedStock) => void
}

function ChannelStockDataTable({ onBreakdownClick }: ChannelStockDataTableProps) {
    const location = useLocation()
    const classes = useStyles()
    const stores = useAppSelector((state) => state.stores)
    const shops = useMemo(() => stores.flatMap((store) => store.shops), [stores])
    const availableChannels = useMemo(() => {
        const channels = shops.map((shop) => shop.channel)
        const uniqueChannels = new Map(channels.map((channel) => [channel.id, channel])).values()

        return Array.from(uniqueChannels)
    }, [shops])

    const dataSource: IDatasource = useMemo(() => {
        return {
            getRows({ successCallback, startRow, endRow }: IGetRowsParams) {
                const searchParams = new URLSearchParams(location.search)

                void api.stock.stockItemsWithStock
                    .query({
                        query: {
                            offset: startRow,
                            limit: endRow - startRow,
                            search: searchParams.get('search') ?? undefined,
                            is_bundle: searchParams.get('is_bundle') ?? undefined,
                        },
                    })
                    .then((data) => {
                        successCallback(data.body.results, data.body.count)
                    })
            },
        }
    }, [location.search])

    const columnDefs: Array<ColDef<Data, string> | ColGroupDef<Data>> = useMemo(() => {
        return [
            {
                field: 'sku',
                headerName: 'SKU',
                pinned: 'left',
                sortable: false,
                cellRenderer: ({ value }: CustomCellRendererProps<Data, Data['sku']>) => {
                    if (!value) {
                        return <Skeleton />
                    }

                    return value
                },
            },
            {
                field: 'name',
                pinned: 'left',
                sortable: false,
                cellRenderer: ({ value }: CustomCellRendererProps<Data, Data['name']>) => {
                    if (typeof value === 'undefined') {
                        return <Skeleton />
                    }

                    return value
                },
            },
            ...availableChannels.map<ColGroupDef<Data>>((channel) => ({
                headerName: channel.name,
                children: shops
                    .filter((shop) => shop.channel.id === channel.id)
                    .map<ColDef<Data, Data['stocks'][0]>>((shop) => ({
                        headerName: shop.shop_name,
                        sortable: false,
                        maxWidth: 300,
                        cellRenderer: ({
                            value,
                            data,
                        }: CustomCellRendererProps<Data, Data['stocks'][0]>) => {
                            if (typeof value === 'undefined') {
                                return <Skeleton />
                            }

                            if (value === null) {
                                return '-'
                            }

                            return (
                                <Stack direction="row" justifyContent="space-between">
                                    {value.calculated_stock.toString()}
                                    <IconButton
                                        onClick={() => {
                                            onBreakdownClick({
                                                channelStockItemId: value.channel_stock_item_id,
                                                isBundle: data?.is_bundle ?? false,
                                            })
                                        }}
                                    >
                                        <LaunchIcon sx={{ opacity: 0 }} fontSize="small" />
                                    </IconButton>
                                </Stack>
                            )
                        },
                        valueGetter: ({ data }) => {
                            if (!data) {
                                return undefined
                            }

                            return (
                                data.stocks.find(
                                    (item) =>
                                        item.channel_id === channel.id && item.shop_id === shop.id
                                ) ?? null
                            )
                        },
                    })),
            })),
        ]
    }, [availableChannels, shops, onBreakdownClick])

    return (
        <Box className="ag-theme-quartz" sx={{ '--ag-font-family': 'Roboto' }}>
            <AgGridReact
                rowModelType="infinite"
                rowClass={classes.row}
                cacheBlockSize={FETCH_LIMIT}
                infiniteInitialRowCount={10}
                datasource={dataSource}
                columnDefs={columnDefs}
            />
        </Box>
    )
}
